/**
 * Render `text` with case-insensitive substring matches of `query` wrapped
 * in `<mark>`. Used to highlight matched terms in story headlines and
 * brief titles after a search.
 *
 * Plain substring matching, not whole-word, so "agent" highlights inside
 * "agentic". Tokenizes the query on whitespace so multi-word queries
 * highlight every word independently — "claude code" → both "claude" and
 * "code" are marked.
 *
 * Performance: text strings are short (titles, ~80–120 chars) so the
 * sequential token scan is fine. We don't compile a regex — we walk the
 * string once and collect ranges, which avoids regex-escape pitfalls.
 */

const MAX_TOKENS = 8;

type Range = { start: number; end: number };

function collectRanges({
  text,
  tokens,
}: {
  text: string;
  tokens: string[];
}): Range[] {
  const lower = text.toLowerCase();
  const ranges: Range[] = [];
  for (const token of tokens) {
    if (token.length === 0) {
      continue;
    }
    let from = 0;
    while (from < lower.length) {
      const idx = lower.indexOf(token, from);
      if (idx === -1) {
        break;
      }
      ranges.push({ start: idx, end: idx + token.length });
      from = idx + token.length;
    }
  }
  if (ranges.length === 0) {
    return ranges;
  }
  // Merge overlapping ranges so we don't nest <mark> tags. We walk the
  // sorted list and track `last` directly instead of re-reading the tail
  // of the merged array on each step.
  ranges.sort((a, b) => a.start - b.start);
  const merged: Range[] = [];
  let last: Range = { ...ranges[0] };
  for (let i = 1; i < ranges.length; i += 1) {
    const cur = ranges[i];
    if (cur.start <= last.end) {
      if (cur.end > last.end) {
        last.end = cur.end;
      }
    } else {
      merged.push(last);
      last = { ...cur };
    }
  }
  merged.push(last);
  return merged;
}

export function HighlightedText({
  text,
  query,
}: {
  text: string;
  query?: string | null;
}) {
  if (!query || query.trim().length === 0) {
    return <>{text}</>;
  }
  const tokens = query
    .toLowerCase()
    .split(/\s+/)
    .filter((t) => t.length > 0)
    .slice(0, MAX_TOKENS);
  const ranges = collectRanges({ text, tokens });
  if (ranges.length === 0) {
    return <>{text}</>;
  }

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  for (const range of ranges) {
    if (range.start > cursor) {
      parts.push(text.slice(cursor, range.start));
    }
    parts.push(
      <mark
        className="rounded-[3px] bg-[var(--explore-accent-teal)]/20 px-0.5 text-[var(--explore-text-primary)]"
        key={`${range.start}-${range.end}`}
      >
        {text.slice(range.start, range.end)}
      </mark>
    );
    cursor = range.end;
  }
  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }
  return <>{parts}</>;
}
